<template>
  <!-- Cuerpo de la card de app/views/tpaga/add_balance.html.erb (el header
       vive en el ERB). Kit Tpaga en app/frontend/css/views/tpaga.scss; lo propio
       (.tpaga-billetera-*) en tpaga_billetera.scss. El bloque de estilos de
       abajo NO es scoped: define .line global, que hoy no usa ningún elemento
       del repo; se deja como está (borrarlo es una decisión aparte). -->
  <div class="tpaga-body">
    <div
      v-if="anyBootstrapError()"
      class="tpaga-notice tpaga-notice--error"
      role="alert"
    >
      <span class="material-icons-sharp" aria-hidden="true">error_outline</span>
      <div class="tpaga-notice__body">
        <strong>No pudimos cargar los datos</strong>
        <p>Revisa tu conexión.</p>
        <div class="tpaga-notice__actions">
          <button
            type="button"
            class="tpaga-link"
            @click="retryFailedBootstraps()"
          >
            Reintentar
          </button>
        </div>
      </div>
    </div>

    <form
      :action="actionUrl"
      method="POST"
      class="tpaga-form"
    >
      <csrf-input />
      <div class="tpaga-field">
        <label
          for="tpaga-billetera-monto"
          class="tpaga-label"
        >
          Monto a abonar<span class="sr-only"> en COP</span>
        </label>
        <div class="tpaga-amount">
          <input
            id="tpaga-billetera-monto"
            type="tel"
            :rawvalue="sourceAmount"
            @input="handleSourceAmountInput"
            v-numeric="sourceAmountMask"
            inputmode="numeric"
            autocomplete="off"
            aria-describedby="tpaga-billetera-minimo tpaga-billetera-costo"
            class="arcadi-form__input arcadi-input tpaga-input" />
          <span class="tpaga-amount__currency" aria-hidden="true">COP</span>
        </div>
        <!-- El mínimo va junto al campo, donde se mira mientras se escribe:
             explica por qué "Confirmar" sigue deshabilitado. -->
        <p id="tpaga-billetera-minimo" class="tpaga-hint">
          El monto mínimo para abonar es {{ AutoNumeric.format(MIN_COST_COP, minimumAmountMask('COP')) }}.
        </p>
        <input type="hidden" name="cost" v-model="sourceAmount" />
      </div>

      <!-- El dato de siempre (como el "2% + IVA" de top_up), sin afirmar quién
           lo cobra ni cómo se aplica: la tasa real sale de TPAGA_COMMISSION y
           TPAGA_IVA (Tpaga::UpdateTpagaPaymentInfo) y el texto que lo explique
           lo tiene que confirmar producto. -->
      <div class="tpaga-notice tpaga-notice--warning" role="status">
        <span class="material-icons-sharp" aria-hidden="true">warning</span>
        <div id="tpaga-billetera-costo" class="tpaga-notice__body">
          <strong>Costo de la transacción</strong>
          <p>2% + IVA.</p>
        </div>
      </div>

      <div class="tpaga-actions">
        <input
          type="submit"
          value="Confirmar"
          :disabled="sourceAmount < MIN_COST_COP"
          aria-describedby="tpaga-billetera-minimo"
          data-disable-with="Conectando con Tpaga…"
          class="tpaga-cta"
        >
        <p class="tpaga-caption">Al confirmar, te llevamos a Tpaga para pagar.</p>
      </div>
    </form>

    <section
      v-if="transferLimits.length || bootstrapState('transferLimits') === 'loading'"
      class="tpaga-panel"
      aria-labelledby="tpaga-billetera-minimos-titulo"
    >
      <div class="tpaga-panel__heading">
        <span class="material-icons-sharp" aria-hidden="true">public</span>
        <div>
          <h2 id="tpaga-billetera-minimos-titulo" class="tpaga-panel__title">Mínimos de envío</h2>
          <p class="tpaga-panel__hint">Lo mínimo que puedes enviar a cada destino. Toca un país para ver sus bancos y observaciones.</p>
        </div>
      </div>
      <p v-if="!transferLimits.length" class="tpaga-hint" role="status">Cargando mínimos…</p>
      <dl v-else class="tpaga-rows tpaga-billetera-minimos">
        <div v-for="minimum in transferLimits" :key="minimum.destinationCountryId">
          <dt>
            <img
              v-if="getCountryPath(minimum.destinationCountryId)"
              :src="getCountryPath(minimum.destinationCountryId)"
              alt=""
              class="tpaga-billetera-bandera"
            />
            <button
              type="button"
              class="tpaga-billetera-minimos__pais"
              aria-haspopup="dialog"
              :aria-label="countryHelpLabel(minimum.destinationCountryId)"
              @click="showModalCountry(minimum.destinationCountryId, $event)"
            >
              <span>{{ getCountryName(minimum.destinationCountryId) }}</span>
              <span class="material-icons-sharp" aria-hidden="true">help_outline</span>
            </button>
          </dt>
          <dd>{{ AutoNumeric.format(minimum.minimumAmount, minimumAmountMask(minimum.currency)) }}</dd>
        </div>
      </dl>
    </section>

    <!-- A body: dentro de la card, su backdrop-filter es el bloque contenedor
         del position: fixed y el velo quedaba encerrado en la card. -->
    <Teleport to="body">
      <modal
        ref="modal"
        :show="showModal"
        @close="closeModal"
        :idCountry="currentCountryIdModal"
        :countryPath="currentCountryPath"
      />
    </Teleport>
  </div>
</template>

<style>
.line {
  border-bottom: 1px solid #ebebeb;
  margin-top: 5px;
}
</style>

<script>
import csrfInput from '../partials/csrf-input.vue';
import SimulatorApi from '../../../api/simulator';
import { humanizeCurrency } from '../../../utils/money_utils';
import AutoNumeric from 'autonumeric';
import Modal from './Modal.vue';

import BootstrapRecovery from '../../../mixins/bootstrap-recovery';

export default {
  mixins: [BootstrapRecovery],
  beforeMount() {
    this.getCountries();
    this.getTransferLimits();
  },
  components: {
    csrfInput,
    Modal
  },
  props:{
    actionUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      // Espejo de TpagaController::TPAGA_MIN_COST_COP. El servidor es la fuente
      // de verdad (revalida en confirm_balance); acá sólo evitamos el submit.
      MIN_COST_COP: 10000,
      sourceAmount: 0,
      transferLimits: [],
      countries: [],
      AutoNumeric,
      showModal: false,
      currentCountryIdModal: 0,
      currentCountryPath: "",
    }
  },
  computed: {
    sourceAmountMask() {
      return {
        allowDecimalPadding: 'floats',
        modifyValueOnWheel: false,
        decimalPlaces: 2,
        decimalPlacesRawValue: null,
        caretPositionOnFocus: 'end',
        selectOnFocus: false,
        decimalCharacter: ',',
        digitGroupSeparator: '.',
        currencySymbol: '',
        currencySymbolPlacement: 's',
        emptyInputBehavior: 'always',
      }
    },
  },
  methods: {
    handleSourceAmountInput(event) {
      this.sourceAmount = Number(event.target.autonumeric.rawValue);
    },
    getTransferLimits() {
      // Sin recuperación, el techo global de 20s de axios convertía una red
      // colombiana lenta en un form muerto sin salida (los mínimos nunca
      // llegaban y Confirmar quedaba deshabilitado). Mismo patrón que el
      // resto del bootstrap front (mixin bootstrap-recovery).
      this.loadWithRecovery('transferLimits', {
        request: () => SimulatorApi.getTransferLimits(),
        validate: data => Array.isArray(data),
        assign: ({ data }) => {
          this.transferLimits = data.filter((item) => {
            return (item.currency == 'COP')
          })
        }
      });
    },
    getCountries(){
      this.loadWithRecovery('countries', {
        request: () => SimulatorApi.getAllCountries(),
        validate: data => Array.isArray(data),
        assign: ({ data }) => {
          this.countries = data;
        }
      });
    },
    getCountryName(countryId){
      const country = this.countries.filter((item) => {
        return (item.id == countryId)
      })
      return country.length > 0 ? country[0].name : "";
    },
    getCountryPath(countryId){
      const country = this.countries.filter((item) => {
        return (item.id == countryId)
      })
      return country.length > 0 ? country[0].urlPath : "";
    },
    minimumAmountMask(currencySymbol) {
      return {
        allowDecimalPadding: 'floats',
        decimalPlaces: 2,
        roundingMethod: 'F',
        decimalPlacesRawValue: null,
        decimalCharacter: ',',
        digitGroupSeparator: '.',
        currencySymbolPlacement: 's',
        emptyInputBehavior: 'always',
        readOnly: true,
        currencySymbol: ` ${humanizeCurrency(currencySymbol)}`,
      };
    },
    showModalCountry(countryId, event){
      this.currentCountryIdModal = countryId;
      this.currentCountryPath = this.getCountryPath(countryId);
      // Foco: al abrir va al botón de cerrar del modal y al cerrar vuelve al
      // país que lo abrió (fuera de la reactividad, como el mixin).
      this.$_modalTrigger = event ? event.currentTarget : null;
      this.showModal = true;
      this.$nextTick(() => {
        const close = this.$refs.modal && this.$refs.modal.$refs.close;
        if (close) close.focus();
      });
    },
    closeModal() {
      this.showModal = false;
      const trigger = this.$_modalTrigger;
      this.$_modalTrigger = null;
      if (trigger && trigger.isConnected) trigger.focus();
    },
    countryHelpLabel(countryId) {
      const name = this.getCountryName(countryId);
      return name ? `${name}: ver bancos y observaciones` : 'Ver bancos y observaciones';
    }
  },
};
</script>
